import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';
import { notificar } from '../utils/notify';
import '../styles/CarteiraContabil.css';

const CarteiraContabil = () => {
  // Estados
  const [dadosCarteira, setDadosCarteira] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [mensagem, setMensagem] = useState('');
  const [filtroRegime, setFiltroRegime] = useState('');

  // Ponte para toasts (react-toastify)
  useEffect(() => { notificar(mensagem); }, [mensagem]);
  const [abaAtiva, setAbaAtiva] = useState('visaoGeral');
  const [filtrosBi, setFiltrosBi] = useState({
    empresa: '', numero_dominio: '', regime: '', operador: '',
    tipo_bi: '', bi_pago: '', link_bi: '', senha_bi: '', drive_cliente: ''
  });
  
  // Estados para controle de acesso
  const [carregandoAcesso, setCarregandoAcesso] = useState(true);
  const [acessoNegado, setAcessoNegado] = useState(false);
  const [dadosUsuario, setDadosUsuario] = useState(null);

  // Cores
  const CORES_REGIMES = {
    'LP': '#3498db',
    'SN': '#2ecc71',
    'LR': '#e74c3c',
    'LRT': '#f39c12',
    'NAO_INFORMADO': '#95a5a6'
  };

  const CORES_COMPLEXIDADE = {
    alta: '#e74c3c',
    media: '#f39c12',
    baixa: '#2ecc71'
  };

  const CORES_GRAFICOS = ['#3498db', '#2ecc71', '#e74c3c', '#f39c12', '#9b59b6', '#1abc9c', '#34495e'];

  // FUNÇÃO PARA NORMALIZAR REGIME
  const normalizarRegime = (regime) => {
    if (!regime) return 'NAO_INFORMADO';
    
    const regimeLower = String(regime).toLowerCase().trim();
    
    if (regimeLower.includes('lucro presumido') || regimeLower === 'lp') return 'LP';
    if (regimeLower.includes('simples nacional') || regimeLower === 'sn') return 'SN';
    if (regimeLower.includes('lucro real trimestral') || regimeLower === 'lrt') return 'LRT';
    if (regimeLower.includes('lucro real') || regimeLower === 'lr') return 'LR';
    
    return 'NAO_INFORMADO';
  };

  // Verificar acesso
  const verificarAcesso = useCallback(async () => {
    setCarregandoAcesso(true);
    const accessToken = localStorage.getItem('access');

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/verificar-grupo/`,
        {
          headers: { Authorization: `Bearer ${accessToken}` }
        }
      );

      if (response.data.success) {
        const { usuario, first_name, grupo, name } = response.data;
        
        setDadosUsuario({
          username: usuario,
          first_name: first_name,
          grupo: grupo,
          name: name
        });

        if (grupo !== 'adm') {
          setAcessoNegado(true);
        } else {
          setAcessoNegado(false);
        }
      } else {
        setAcessoNegado(true);
      }
    } catch (error) {
      console.error('Erro ao verificar acesso:', error);
      setAcessoNegado(true);
    } finally {
      setCarregandoAcesso(false);
    }
  }, []);

  // Carregar dados da carteira
  const carregarDados = useCallback(async () => {
    setCarregando(true);
    const accessToken = localStorage.getItem('access');

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/carteira-contabil/`,
        {
          headers: { Authorization: `Bearer ${accessToken}` }
        }
      );

      if (response.data.success) {
        setDadosCarteira(response.data);
      } else {
        setMensagem('Erro ao carregar dados da carteira');
      }
      
      setCarregando(false);
    } catch (error) {
      console.error('Erro ao carregar carteira:', error);
      setMensagem('Erro ao carregar dados da carteira');
      setCarregando(false);
    }
  }, []);

  // Effects
  useEffect(() => {
    verificarAcesso();
  }, [verificarAcesso]);

  useEffect(() => {
    if (!acessoNegado && !carregandoAcesso) {
      carregarDados();
    }
  }, [carregarDados, acessoNegado, carregandoAcesso]);

  // APLICAR FILTRO DE REGIME COM USEMEMO
// APLICAR FILTRO DE REGIME COM USEMEMO
const dadosCarteiraFiltrados = useMemo(() => {
  if (!dadosCarteira) return null;
  if (!filtroRegime || filtroRegime === '') return dadosCarteira;

  console.log('Aplicando filtro:', filtroRegime); // Debug

  // Criar cópia dos dados para filtrar
  const dadosFiltrados = {
    success: true,
    total_geral: 0,
    regimes: {},
    complexidade: { alta: 0, media: 0, baixa: 0, nao_informado: 0, percentuais: {}, detalhes: [] },
    bi: { com_bi: 0, sem_bi: 0, potencial_minimo: 0, empresas_com_bi: [], empresas_sem_bi: [] },
    erp: { com_erp: 0, sem_erp: 0, tipos_erp: {}, erp_com_bi: 0, erp_sem_bi: 0, detalhes: [] },
    auditoria: { externa: 0, interna: 0, ambas: 0, nenhuma: 0, detalhes: [] },
    atendimento_por_operador: {},
    fechamentos_por_dt: [],
    complexidade_por_operador: []
  };

  // ========================================
  // 1. FILTRAR REGIMES
  // ========================================
  if (dadosCarteira.regimes && dadosCarteira.regimes[filtroRegime]) {
    dadosFiltrados.regimes[filtroRegime] = dadosCarteira.regimes[filtroRegime];
    dadosFiltrados.total_geral = dadosCarteira.regimes[filtroRegime].total;
    
    // Pegar lista de empresas do regime filtrado
    const empresasDoRegime = dadosCarteira.regimes[filtroRegime].empresas;
    const empresasSet = new Set(empresasDoRegime.map(e => e.numero_dominio));
    
    console.log('Total de empresas no regime:', empresasSet.size); // Debug

    // ========================================
    // 2. FILTRAR COMPLEXIDADE
    // ========================================
    if (dadosCarteira.complexidade && dadosCarteira.complexidade.detalhes) {
      dadosCarteira.complexidade.detalhes.forEach(item => {
        // Verificar se a empresa pertence ao regime filtrado
        const empresaRegime = normalizarRegime(item.regime);
        
        if (empresaRegime === filtroRegime) {
          const complexidade = String(item.complexidade || '').trim().toLowerCase();
          
          if (complexidade.includes('alta')) {
            dadosFiltrados.complexidade.alta += 1;
          } else if (complexidade.includes('media') || complexidade.includes('média')) {
            dadosFiltrados.complexidade.media += 1;
          } else if (complexidade.includes('baixa')) {
            dadosFiltrados.complexidade.baixa += 1;
          } else {
            dadosFiltrados.complexidade.nao_informado += 1;
          }
          
          dadosFiltrados.complexidade.detalhes.push(item);
        }
      });
      
      // Calcular percentuais
      const totalComplexidade = dadosFiltrados.complexidade.alta + 
                                dadosFiltrados.complexidade.media + 
                                dadosFiltrados.complexidade.baixa;
      
      if (totalComplexidade > 0) {
        dadosFiltrados.complexidade.percentuais = {
          alta: Math.round((dadosFiltrados.complexidade.alta / totalComplexidade) * 100 * 10) / 10,
          media: Math.round((dadosFiltrados.complexidade.media / totalComplexidade) * 100 * 10) / 10,
          baixa: Math.round((dadosFiltrados.complexidade.baixa / totalComplexidade) * 100 * 10) / 10
        };
      }
    }

    // ========================================
// ========================================
// 3. FILTRAR BI (ATUALIZADO COM DISTRIBUIÇÃO)
// ========================================
if (dadosCarteira.bi) {
  dadosFiltrados.bi.distribuicao_tipos_bi = {}; // Inicializar

  // Filtrar empresas COM BI
  if (dadosCarteira.bi.empresas_com_bi) {
    dadosCarteira.bi.empresas_com_bi.forEach(item => {
      const empresaRegime = normalizarRegime(item.regime);
      if (empresaRegime === filtroRegime) {
        dadosFiltrados.bi.com_bi += 1;
        dadosFiltrados.bi.empresas_com_bi.push(item);

        // Contar distribuição de tipos de BI
        const tipoBi = item.tipo_bi || 'Não informado';
        if (!dadosFiltrados.bi.distribuicao_tipos_bi[tipoBi]) {
          dadosFiltrados.bi.distribuicao_tipos_bi[tipoBi] = {
            total: 0,
            empresas: []
          };
        }
        dadosFiltrados.bi.distribuicao_tipos_bi[tipoBi].total += 1;
        dadosFiltrados.bi.distribuicao_tipos_bi[tipoBi].empresas.push({
          empresa: item.empresa,
          operador: item.operador,
          regime: item.regime
        });
      }
    });
  }
  
  // Filtrar empresas SEM BI
  if (dadosCarteira.bi.empresas_sem_bi) {
    dadosCarteira.bi.empresas_sem_bi.forEach(item => {
      const empresaRegime = normalizarRegime(item.regime);
      if (empresaRegime === filtroRegime) {
        dadosFiltrados.bi.sem_bi += 1;
        dadosFiltrados.bi.empresas_sem_bi.push(item);
      }
    });
  }
  
  // Recalcular potencial mínimo
  dadosFiltrados.bi.potencial_minimo = dadosFiltrados.bi.sem_bi * 900;
}
// dadosFiltrados.tipos_bi = {};
// if (dadosCarteira.tipos_bi) {
//   // Se temos empresas_com_bi filtradas, contar seus tipos
dadosFiltrados.tipos_bi = {};
if (dadosFiltrados.bi.empresas_com_bi && dadosFiltrados.bi.empresas_com_bi.length > 0) {
  // Contar tipos de BI das empresas filtradas
  dadosFiltrados.bi.empresas_com_bi.forEach(empresa => {
    const tipo = empresa.tipo_bi;
    if (tipo && tipo !== '-' && tipo.toLowerCase() !== 'nan' && tipo.toLowerCase() !== 'null') {
      if (!dadosFiltrados.tipos_bi[tipo]) {
        dadosFiltrados.tipos_bi[tipo] = 0;
      }
      dadosFiltrados.tipos_bi[tipo] += 1;
    }
  });
} else if (dadosCarteira.tipos_bi) {
  // Sem filtro, usar dados originais do backend
  dadosFiltrados.tipos_bi = dadosCarteira.tipos_bi;
}

console.log('Tipos de BI filtrados:', dadosFiltrados.tipos_bi); 
// 4. FILTRAR ERP (ATUALIZADO COM DISTRIBUIÇÃO)
// ========================================
if (dadosCarteira.erp && dadosCarteira.erp.detalhes) {
  dadosFiltrados.erp.distribuicao_tipos_erp = {}; // Inicializar

  dadosCarteira.erp.detalhes.forEach(item => {
    const empresaRegime = normalizarRegime(item.regime || '');
    
    if (empresaRegime === filtroRegime) {
      dadosFiltrados.erp.com_erp += 1;
      
      // Contar tipos de ERP
      if (item.erp && item.erp !== '-') {
        if (!dadosFiltrados.erp.tipos_erp[item.erp]) {
          dadosFiltrados.erp.tipos_erp[item.erp] = 0;
        }
        dadosFiltrados.erp.tipos_erp[item.erp] += 1;

        // Contar distribuição detalhada de tipos de ERP
        if (!dadosFiltrados.erp.distribuicao_tipos_erp[item.erp]) {
          dadosFiltrados.erp.distribuicao_tipos_erp[item.erp] = {
            total: 0,
            empresas: []
          };
        }
        dadosFiltrados.erp.distribuicao_tipos_erp[item.erp].total += 1;
        dadosFiltrados.erp.distribuicao_tipos_erp[item.erp].empresas.push({
          empresa: item.empresa,
          operador: item.operador,
          regime: item.regime,
          tem_bi: item.tem_bi
        });
      }
      
      // Contar ERP com/sem BI
      if (item.tem_bi) {
        dadosFiltrados.erp.erp_com_bi += 1;
      } else {
        dadosFiltrados.erp.erp_sem_bi += 1;
      }
      
      dadosFiltrados.erp.detalhes.push(item);
    }
  });

// ========================================
// 4.1 FILTRAR MOTIVOS SEM ERP (NOVO)
// ========================================
dadosFiltrados.motivos_sem_erp = {};
if (dadosCarteira.motivos_sem_erp) {
  // Para cada empresa SEM ERP no regime filtrado
  Object.keys(dadosCarteira.motivos_sem_erp).forEach(motivo => {
    dadosFiltrados.motivos_sem_erp[motivo] = 0;
  });
  
  // Contar motivos das empresas filtradas
  // Como não temos lista de empresas sem ERP detalhada, vamos calcular proporcionalmente
  const totalOriginalSemErp = dadosCarteira.total_geral - dadosCarteira.erp.com_erp;
  const totalFiltradoSemErp = dadosFiltrados.erp.sem_erp;
  
  if (totalOriginalSemErp > 0) {
    Object.entries(dadosCarteira.motivos_sem_erp).forEach(([motivo, qtd]) => {
      const proporcao = totalFiltradoSemErp / totalOriginalSemErp;
      dadosFiltrados.motivos_sem_erp[motivo] = Math.round(qtd * proporcao);
    });
  }
} else if (!filtroRegime) {
  dadosFiltrados.motivos_sem_erp = dadosCarteira.motivos_sem_erp || {};
}

console.log('Motivos SEM ERP filtrados:', dadosFiltrados.motivos_sem_erp);

  
  // Calcular empresas SEM ERP (total - com_erp)
  dadosFiltrados.erp.sem_erp = dadosFiltrados.total_geral - dadosFiltrados.erp.com_erp;
}

    // ========================================
    // 5. FILTRAR AUDITORIA
    // ========================================
    if (dadosCarteira.auditoria && dadosCarteira.auditoria.detalhes) {
      const empresasComAuditoria = new Set();
      
      dadosCarteira.auditoria.detalhes.forEach(item => {
        const empresaRegime = normalizarRegime(item.regime || '');
        
        if (empresaRegime === filtroRegime) {
          const temExterna = String(item.auditoria_externa || '').toUpperCase() === 'SIM';
          const temInterna = String(item.auditoria_interna || '').toUpperCase() === 'SIM';
          
          if (temExterna && temInterna) {
            dadosFiltrados.auditoria.ambas += 1;
          } else if (temExterna) {
            dadosFiltrados.auditoria.externa += 1;
          } else if (temInterna) {
            dadosFiltrados.auditoria.interna += 1;
          }
          
          empresasComAuditoria.add(item.empresa);
          dadosFiltrados.auditoria.detalhes.push(item);
        }
      });
      
      // Calcular empresas SEM auditoria
      dadosFiltrados.auditoria.nenhuma = dadosFiltrados.total_geral - empresasComAuditoria.size;
    }

    // ========================================
    // 6. FILTRAR ATENDIMENTO POR OPERADOR
    // ========================================
    if (dadosCarteira.atendimento_por_operador) {
      Object.entries(dadosCarteira.atendimento_por_operador).forEach(([operador, operadorData]) => {
        const clientesFiltrados = operadorData.clientes.filter(cliente => {
          const regimeNorm = normalizarRegime(cliente.regime);
          return regimeNorm === filtroRegime;
        });

        if (clientesFiltrados.length > 0) {
          const regimes = { LP: 0, SN: 0, LR: 0, LRT: 0, NAO_INFORMADO: 0 };
          const complexidade = { alta: 0, media: 0, baixa: 0 };
          
          clientesFiltrados.forEach(cliente => {
            const regimeNorm = normalizarRegime(cliente.regime);
            if (regimes.hasOwnProperty(regimeNorm)) {
              regimes[regimeNorm]++;
            }
          });

          dadosFiltrados.atendimento_por_operador[operador] = {
            total_clientes: clientesFiltrados.length,
            clientes: clientesFiltrados,
            regimes: regimes,
            complexidade: complexidade
          };
        }
      });
    }

    // ========================================
    // 7. RECALCULAR COMPLEXIDADE POR OPERADOR
    // ========================================
    if (Object.keys(dadosFiltrados.atendimento_por_operador).length > 0) {
      // Primeiro, precisamos recalcular a complexidade de cada operador
      // baseado nos clientes filtrados
      if (dadosCarteira.complexidade && dadosCarteira.complexidade.detalhes) {
        Object.entries(dadosFiltrados.atendimento_por_operador).forEach(([operador, dados]) => {
          const complexidade = { alta: 0, media: 0, baixa: 0 };
          
          // Para cada cliente do operador no regime filtrado
          dados.clientes.forEach(cliente => {
            // Buscar a complexidade desse cliente nos detalhes
            const detalhesCliente = dadosCarteira.complexidade.detalhes.find(
              d => d.empresa === cliente.empresa && d.operador === operador
            );
            
            if (detalhesCliente) {
              const comp = String(detalhesCliente.complexidade || '').trim().toLowerCase();
              if (comp.includes('alta')) {
                complexidade.alta += 1;
              } else if (comp.includes('media') || comp.includes('média')) {
                complexidade.media += 1;
              } else if (comp.includes('baixa')) {
                complexidade.baixa += 1;
              }
            }
          });
          
          // Atualizar complexidade do operador
          dadosFiltrados.atendimento_por_operador[operador].complexidade = complexidade;
        });
      }
      
      // Criar array de complexidade por operador
      dadosFiltrados.complexidade_por_operador = Object.entries(dadosFiltrados.atendimento_por_operador)
        .map(([operador, dados]) => {
          const total = dados.total_clientes;
          const percentuais = total > 0 ? {
            alta: Math.round((dados.complexidade.alta / total) * 100 * 10) / 10,
            media: Math.round((dados.complexidade.media / total) * 100 * 10) / 10,
            baixa: Math.round((dados.complexidade.baixa / total) * 100 * 10) / 10
          } : { alta: 0, media: 0, baixa: 0 };
          
          return {
            operador: operador,
            total_clientes: total,
            percentuais: percentuais,
            quantidades: dados.complexidade
          };
        })
        .sort((a, b) => b.total_clientes - a.total_clientes);
    }

    // ========================================
    // 8. FILTRAR FECHAMENTOS POR DT
    // ========================================
    if (dadosCarteira.fechamentos_por_dt) {
      dadosFiltrados.fechamentos_por_dt = dadosCarteira.fechamentos_por_dt.filter(item => 
        dadosFiltrados.atendimento_por_operador.hasOwnProperty(item.operador)
      );
    }

    console.log('Dados filtrados:', {
      total: dadosFiltrados.total_geral,
      complexidade: dadosFiltrados.complexidade,
      bi: `${dadosFiltrados.bi.com_bi} com BI / ${dadosFiltrados.bi.sem_bi} sem BI`,
      erp: `${dadosFiltrados.erp.com_erp} com ERP / ${dadosFiltrados.erp.sem_erp} sem ERP`,
      auditoria: dadosFiltrados.auditoria
    });

  }

  return dadosFiltrados;
}, [dadosCarteira, filtroRegime]);



  // Preparar dados para gráficos (USAR dadosCarteiraFiltrados)
  const dadosRegimes = dadosCarteiraFiltrados?.regimes ? Object.entries(dadosCarteiraFiltrados.regimes)
    .filter(([key, value]) => value.total > 0)
    .map(([key, value]) => ({
      name: value.nome,
      value: value.total,
      color: CORES_REGIMES[key] || '#95a5a6'
    })) : [];

  const dadosComplexidade = dadosCarteiraFiltrados?.complexidade ? [
    { name: 'Alta', value: dadosCarteiraFiltrados.complexidade.alta, color: CORES_COMPLEXIDADE.alta },
    { name: 'Média', value: dadosCarteiraFiltrados.complexidade.media, color: CORES_COMPLEXIDADE.media },
    { name: 'Baixa', value: dadosCarteiraFiltrados.complexidade.baixa, color: CORES_COMPLEXIDADE.baixa }
  ].filter(item => item.value > 0) : [];

  const dadosBi = dadosCarteiraFiltrados?.bi ? [
    { name: 'Com BI', value: dadosCarteiraFiltrados.bi.com_bi, color: '#2ecc71' },
    { name: 'Sem BI', value: dadosCarteiraFiltrados.bi.sem_bi, color: '#e74c3c' }
  ] : [];

  const dadosErp = dadosCarteiraFiltrados?.erp ? [
    { name: 'Com ERP', value: dadosCarteiraFiltrados.erp.com_erp, color: '#3498db' },
    { name: 'Sem ERP', value: dadosCarteiraFiltrados.erp.sem_erp, color: '#95a5a6' }
  ] : [];

  const dadosErpComBi = dadosCarteiraFiltrados?.erp ? [
    { name: 'ERP + BI', value: dadosCarteiraFiltrados.erp.erp_com_bi, color: '#27ae60' },
    { name: 'ERP sem BI', value: dadosCarteiraFiltrados.erp.erp_sem_bi, color: '#e67e22' }
  ] : [];

  const dadosAtendimentoOperador = dadosCarteiraFiltrados?.atendimento_por_operador 
    ? Object.entries(dadosCarteiraFiltrados.atendimento_por_operador)
        .map(([operador, dados]) => ({
          operador,
          total: dados.total_clientes
        }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 10)
    : [];

  // Loading acesso
  if (carregandoAcesso) {
    return (
      <div className="carteira-contabil-page">
        <div className="container mt-5 text-center">
          <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Verificando acesso...</span>
          </div>
          <p className="text-muted">Verificando permissões de acesso...</p>
        </div>
      </div>
    );
  }

  // Acesso negado
  if (acessoNegado) {
    return (
      <div className="carteira-contabil-page">
        <div className="container mt-5 text-center">
          <div className="alert alert-danger p-5 shadow-lg" role="alert" style={{ maxWidth: '500px', margin: '0 auto', borderRadius: '16px' }}>
            <i className="fas fa-lock fa-4x mb-4 text-danger"></i>
            <h2 className="mb-3">Acesso Negado</h2>
            <p className="mb-4">
              Você não tem permissão para acessar a <strong>Carteira Contábil</strong>.
            </p>
            <hr />
            <div className="text-muted mb-4">
              <small>
                <i className="fas fa-user me-2"></i>
                Usuário: <strong>{dadosUsuario?.username || 'Não identificado'}</strong>
              </small>
              <br />
              <small>
                <i className="fas fa-users me-2"></i>
                Grupo: <strong>{dadosUsuario?.grupo || 'Não definido'}</strong>
              </small>
            </div>
            <button 
              className="btn btn-outline-primary btn-lg"
              onClick={() => window.location.href = '/home'}
            >
              <i className="fas fa-home me-2"></i>
              Voltar para página inicial
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading dados
  if (carregando) {
    return (
      <div className="carteira-contabil-page">
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
          <p className="mt-3">Carregando análise da carteira...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="carteira-contabil-page">
      <div className="container-fluid py-4">
        {/* Header */}
        <div className="header-section mb-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h1 className="titulo-pagina">
                <i className="fas fa-briefcase header-icon me-3"></i>
                Carteira Contábil
              </h1>
              <p className="page-subtitle">Análise completa da carteira de clientes por regime tributário</p>
            </div>
            <button 
              className="btn btn-outline-primary"
              onClick={carregarDados}
              disabled={carregando}
            >
              <i className={`fas fa-sync-alt me-2 ${carregando ? 'fa-spin' : ''}`}></i>
              Atualizar Dados
            </button>
          </div>
        </div>

        {/* Mensagens agora são exibidas como toast (react-toastify) */}

        {/* Filtro de Regime */}
        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col-md-3">
                <label className="form-label fw-semibold">
                  <i className="fas fa-filter me-2 text-primary"></i>
                  Filtrar por Regime Tributário
                </label>
              </div>
              <div className="col-md-7">
                <select 
                  className="form-select"
                  value={filtroRegime}
                  onChange={(e) => setFiltroRegime(e.target.value)}
                >
                  <option value="">Todos os Regimes</option>
                  <option value="LP">Lucro Presumido</option>
                  <option value="SN">Simples Nacional</option>
                  <option value="LR">Lucro Real</option>
                  <option value="LRT">Lucro Real Trimestral</option>
                </select>
              </div>
              <div className="col-md-2">
                <button 
                  className="btn btn-secondary w-100"
                  onClick={() => setFiltroRegime('')}
                >
                  <i className="fas fa-eraser me-2"></i>
                  Limpar
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* KPIs - Total por Regime */}
        <div className="row g-4 mb-4">
          {dadosCarteiraFiltrados?.regimes && Object.entries(dadosCarteiraFiltrados.regimes)
            .filter(([key, regime]) => regime.total > 0)
            .map(([key, regime]) => (
              <div key={key} className="col-xl-3 col-md-6">
                <div 
                  className="kpi-card-regime"
                  style={{ borderLeftColor: CORES_REGIMES[key] || '#95a5a6' }}
                >
                  <div 
                    className="kpi-icon" 
                    style={{ 
                      backgroundColor: `${CORES_REGIMES[key] || '#95a5a6'}20`, 
                      color: CORES_REGIMES[key] || '#95a5a6' 
                    }}
                  >
                    <i className="fas fa-building"></i>
                  </div>
                  <div className="kpi-content">
                    <h3 className="kpi-value" style={{ color: CORES_REGIMES[key] || '#95a5a6' }}>
                      {regime.total}
                    </h3>
                    <p className="kpi-label">{regime.nome}</p>
                  </div>
                </div>
              </div>
            ))}
        </div>

        {/* Abas de navegação */}
        <ul className="nav nav-tabs nav-tabs-custom mb-4">
          <li className="nav-item">
            <button 
              className={`nav-link ${abaAtiva === 'visaoGeral' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('visaoGeral')}
            >
              <i className="fas fa-th-large me-2"></i>
              Visão Geral
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link ${abaAtiva === 'complexidade' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('complexidade')}
            >
              <i className="fas fa-layer-group me-2"></i>
              Complexidade
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link ${abaAtiva === 'bi-erp' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('bi-erp')}
            >
              <i className="fas fa-chart-line me-2"></i>
              BI & ERP
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link ${abaAtiva === 'operadores' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('operadores')}
            >
              <i className="fas fa-users me-2"></i>
              Operadores
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link ${abaAtiva === 'auditoria' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('auditoria')}
            >
              <i className="fas fa-clipboard-check me-2"></i>
              Auditoria
            </button>
          </li>
        </ul>

        {/* Conteúdo das Abas */}
        <div className="tab-content">
          {/* ABA VISÃO GERAL */}
          {abaAtiva === 'visaoGeral' && (
            <div className="tab-pane-custom">
              {/* Gráficos principais */}
              <div className="row g-4 mb-4">
                {/* Distribuição por Regime */}
                <div className="col-lg-6">
                  <div className="card shadow-sm h-100">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-chart-pie me-2 text-primary"></i>
                        Distribuição por Regime Tributário
                      </h5>
                    </div>
                    <div className="card-body">
                      {dadosRegimes.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={dadosRegimes}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={100}
                              innerRadius={60}
                              paddingAngle={3}
                              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            >
                              {dadosRegimes.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => [value, 'Clientes']} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center text-muted py-5">
                          <i className="fas fa-inbox fa-3x mb-3"></i>
                          <p>Nenhum dado disponível</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Complexidade */}
                <div className="col-lg-6">
                  <div className="card shadow-sm h-100">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-layer-group me-2 text-primary"></i>
                        Distribuição por Complexidade
                      </h5>
                    </div>
                    <div className="card-body">
                      {dadosComplexidade.length > 0 ? (
                        <>
                          <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                              <Pie
                                data={dadosComplexidade}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                innerRadius={50}
                                paddingAngle={3}
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                              >
                                {dadosComplexidade.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(value) => [value, 'Clientes']} />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                          
                          {dadosCarteiraFiltrados?.complexidade?.percentuais && (
                            <div className="mt-3 text-center">
                              <small className="text-muted d-block mb-2">
                                <strong>Percentuais:</strong>
                              </small>
                              <div className="d-flex justify-content-center gap-3">
                                <span className="badge bg-danger">
                                  Alta: {dadosCarteiraFiltrados.complexidade.percentuais.alta}%
                                </span>
                                <span className="badge bg-warning text-dark">
                                  Média: {dadosCarteiraFiltrados.complexidade.percentuais.media}%
                                </span>
                                <span className="badge bg-success">
                                  Baixa: {dadosCarteiraFiltrados.complexidade.percentuais.baixa}%
                                </span>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-center text-muted py-5">
                          <i className="fas fa-inbox fa-3x mb-3"></i>
                          <p>Nenhum dado disponível</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Resumo Numérico */}
              <div className="card shadow-sm">
                <div className="card-header bg-white">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-list-ol me-2 text-primary"></i>
                    Resumo Geral da Carteira
                    {filtroRegime && (
                      <span className="badge bg-primary ms-2 fs-6">
                        Filtrado: {dadosCarteiraFiltrados?.regimes[filtroRegime]?.nome}
                      </span>
                    )}
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-3 col-6">
                      <div className="stat-item stat-primary">
                        <span className="stat-value">{dadosCarteiraFiltrados?.total_geral || 0}</span>
                        <span className="stat-label">Total de Clientes</span>
                      </div>
                    </div>
                    <div className="col-md-3 col-6">
                      <div className="stat-item stat-success">
                        <span className="stat-value">{dadosCarteiraFiltrados?.bi?.com_bi || 0}</span>
                        <span className="stat-label">Clientes com BI</span>
                      </div>
                    </div>
                    <div className="col-md-3 col-6">
                      <div className="stat-item stat-info">
                        <span className="stat-value">{dadosCarteiraFiltrados?.erp?.com_erp || 0}</span>
                        <span className="stat-label">Clientes com ERP</span>
                      </div>
                    </div>
                    <div className="col-md-3 col-6">
                      <div className="stat-item stat-warning">
                        <span className="stat-value">
                          R$ {dadosCarteiraFiltrados?.bi?.potencial_minimo?.toLocaleString('pt-BR') || 0}
                        </span>
                        <span className="stat-label">Potencial Mínimo BI</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ABA COMPLEXIDADE */}
          {abaAtiva === 'complexidade' && (
            <div className="tab-pane-custom">
              {/* Complexidade por Operador */}
              <div className="row g-4 mb-4">
                <div className="col-12">
                  <div className="card shadow-sm">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-chart-bar me-2 text-primary"></i>
                        Complexidade por Operador
                      </h5>
                    </div>
                    <div className="card-body">
                      {dadosCarteiraFiltrados?.complexidade_por_operador && dadosCarteiraFiltrados.complexidade_por_operador.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-hover align-middle">
                            <thead className="table-light">
                              <tr>
                                <th>Operador</th>
                                <th className="text-center">Total Clientes</th>
                                <th className="text-center">Alta</th>
                                <th className="text-center">Média</th>
                                <th className="text-center">Baixa</th>
                                <th>Distribuição Percentual</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dadosCarteiraFiltrados.complexidade_por_operador.map((op, index) => (
                                <tr key={index}>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <div 
                                        className="operador-avatar me-2"
                                        style={{ backgroundColor: CORES_GRAFICOS[index % CORES_GRAFICOS.length] }}
                                      >
                                        {op.operador.charAt(0).toUpperCase()}
                                      </div>
                                      <span className="fw-semibold">{op.operador}</span>
                                    </div>
                                  </td>
                                  <td className="text-center">
                                    <span className="badge bg-secondary fs-6">{op.total_clientes}</span>
                                  </td>
                                  <td className="text-center">
                                    <span className="badge bg-danger">{op.quantidades.alta}</span>
                                    <br />
                                    <small className="text-muted">{op.percentuais.alta}%</small>
                                  </td>
                                  <td className="text-center">
                                    <span className="badge bg-warning text-dark">{op.quantidades.media}</span>
                                    <br />
                                    <small className="text-muted">{op.percentuais.media}%</small>
                                  </td>
                                  <td className="text-center">
                                    <span className="badge bg-success">{op.quantidades.baixa}</span>
                                    <br />
                                    <small className="text-muted">{op.percentuais.baixa}%</small>
                                  </td>
                                  <td>
                                    <div className="progress" style={{ height: '25px' }}>
                                      <div 
                                        className="progress-bar bg-danger" 
                                        style={{ width: `${op.percentuais.alta}%` }}
                                        title={`Alta: ${op.percentuais.alta}%`}
                                      >
                                        {op.percentuais.alta > 10 && `${op.percentuais.alta}%`}
                                      </div>
                                      <div 
                                        className="progress-bar bg-warning" 
                                        style={{ width: `${op.percentuais.media}%` }}
                                        title={`Média: ${op.percentuais.media}%`}
                                      >
                                        {op.percentuais.media > 10 && `${op.percentuais.media}%`}
                                      </div>
                                      <div 
                                        className="progress-bar bg-success" 
                                        style={{ width: `${op.percentuais.baixa}%` }}
                                        title={`Baixa: ${op.percentuais.baixa}%`}
                                      >
                                        {op.percentuais.baixa > 10 && `${op.percentuais.baixa}%`}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center text-muted py-5">
                          <i className="fas fa-inbox fa-3x mb-3"></i>
                          <p>Nenhum dado disponível</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Gráfico de barras - Complexidade por Operador (Top 10) */}
              <div className="row g-4">
                <div className="col-12">
                  <div className="card shadow-sm">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-chart-bar me-2 text-primary"></i>
                        Top 10 Operadores - Distribuição de Complexidade
                      </h5>
                    </div>
                    <div className="card-body">
                      {dadosCarteiraFiltrados?.complexidade_por_operador && dadosCarteiraFiltrados.complexidade_por_operador.length > 0 ? (
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart 
                            data={dadosCarteiraFiltrados.complexidade_por_operador.slice(0, 10)}
                            margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="operador" 
                              angle={-45} 
                              textAnchor="end" 
                              height={100}
                              interval={0}
                            />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="quantidades.alta" name="Alta" fill={CORES_COMPLEXIDADE.alta} stackId="a" />
                            <Bar dataKey="quantidades.media" name="Média" fill={CORES_COMPLEXIDADE.media} stackId="a" />
                            <Bar dataKey="quantidades.baixa" name="Baixa" fill={CORES_COMPLEXIDADE.baixa} stackId="a" />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center text-muted py-5">
                          <i className="fas fa-inbox fa-3x mb-3"></i>
                          <p>Nenhum dado disponível</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ABA BI & ERP */}
{/* ABA BI & ERP */}
{abaAtiva === 'bi-erp' && (
  <div className="tab-pane-custom">
    {/* KPIs BI & ERP */}
    <div className="row g-4 mb-4">
      <div className="col-lg-3 col-md-6">
        <div className="kpi-card kpi-success">
          <div className="kpi-icon">
            <i className="fas fa-chart-line"></i>
          </div>
          <div className="kpi-content">
            <h3 className="kpi-value">{dadosCarteiraFiltrados?.bi?.com_bi || 0}</h3>
            <p className="kpi-label">Clientes com BI</p>
          </div>
        </div>
      </div>
      <div className="col-lg-3 col-md-6">
        <div className="kpi-card kpi-danger">
          <div className="kpi-icon">
            <i className="fas fa-chart-line"></i>
          </div>
          <div className="kpi-content">
            <h3 className="kpi-value">{dadosCarteiraFiltrados?.bi?.sem_bi || 0}</h3>
            <p className="kpi-label">Clientes sem BI</p>
          </div>
        </div>
      </div>
      <div className="col-lg-3 col-md-6">
        <div className="kpi-card kpi-info">
          <div className="kpi-icon">
            <i className="fas fa-desktop"></i>
          </div>
          <div className="kpi-content">
            <h3 className="kpi-value">{dadosCarteiraFiltrados?.erp?.com_erp || 0}</h3>
            <p className="kpi-label">Clientes com ERP</p>
          </div>
        </div>
      </div>
      <div className="col-lg-3 col-md-6">
        <div className="kpi-card kpi-warning">
          <div className="kpi-icon">
            <i className="fas fa-dollar-sign"></i>
          </div>
          <div className="kpi-content">
            <h3 className="kpi-value">
              R$ {(dadosCarteiraFiltrados?.bi?.potencial_minimo || 0).toLocaleString('pt-BR')}
            </h3>
            <p className="kpi-label">Potencial Mínimo BI</p>
          </div>
        </div>
      </div>
    </div>

    {/* Gráficos Pizza (3 cards) */}
    <div className="row g-4 mb-4">
      {/* BI */}
      <div className="col-lg-4">
        <div className="card shadow-sm h-100">
          <div className="card-header bg-white">
            <h5 className="card-title mb-0">
              <i className="fas fa-chart-pie me-2 text-success"></i>
              Distribuição BI
            </h5>
          </div>
          <div className="card-body">
            {dadosBi.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={dadosBi}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {dadosBi.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center text-muted py-5">
                <p>Sem dados</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ERP */}
      <div className="col-lg-4">
        <div className="card shadow-sm h-100">
          <div className="card-header bg-white">
            <h5 className="card-title mb-0">
              <i className="fas fa-chart-pie me-2 text-info"></i>
              Distribuição ERP
            </h5>
          </div>
          <div className="card-body">
            {dadosErp.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={dadosErp}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {dadosErp.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center text-muted py-5">
                <p>Sem dados</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ERP com BI */}
      <div className="col-lg-4">
        <div className="card shadow-sm h-100">
          <div className="card-header bg-white">
            <h5 className="card-title mb-0">
              <i className="fas fa-chart-pie me-2 text-primary"></i>
              ERP x BI
            </h5>
          </div>
          <div className="card-body">
            {dadosErpComBi.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={dadosErpComBi}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {dadosErpComBi.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center text-muted py-5">
                <p>Sem dados</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* DISTRIBUIÇÃO DE TIPOS */}
    <div className="row g-4 mb-4">
<div className="col-12">
  <div className="card shadow-sm">
    <div className="card-header bg-white">
      <h5 className="card-title mb-0">
        <i className="fas fa-chart-bar me-2 text-info"></i>
        Distribuição por Tipo de ERP
      </h5>
    </div>
    <div className="card-body">
      {dadosCarteiraFiltrados?.erp?.tipos_erp && Object.keys(dadosCarteiraFiltrados.erp.tipos_erp).length > 0 ? (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart 
            data={Object.entries(dadosCarteiraFiltrados.erp.tipos_erp)
              .map(([tipo, qtd]) => ({ tipo, qtd }))
              .sort((a, b) => b.qtd - a.qtd)}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis hide />
            <YAxis />
            <Tooltip 
              formatter={(value, name, props) => [value, props.payload.tipo]}
              labelFormatter={() => ''}
            />
            <Bar dataKey="qtd" name="Quantidade" fill="#3498db">
              {Object.entries(dadosCarteiraFiltrados.erp.tipos_erp).map((entry, index) => (
                <Cell key={`cell-${index}`} fill={CORES_GRAFICOS[index % CORES_GRAFICOS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="text-center text-muted py-5">
          <i className="fas fa-inbox fa-3x mb-3"></i>
          <p>Nenhum sistema ERP registrado</p>
        </div>
      )}
    </div>
  </div>
</div>

      {/* Distribuição de Tipos de BI */}
      {/* Distribuição de Tipos de BI */}
        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white">
              <h5 className="card-title mb-0">
                <i className="fas fa-chart-bar me-2 text-success"></i>
                Distribuição por Tipo de BI
              </h5>
            </div>
            <div className="card-body">
            {dadosCarteiraFiltrados?.tipos_bi && Object.keys(dadosCarteiraFiltrados.tipos_bi).length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart 
                  data={Object.entries(dadosCarteiraFiltrados.tipos_bi)
                    .map(([tipo, qtd]) => ({ tipo, qtd }))
                    .sort((a, b) => b.qtd - a.qtd)}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis hide />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name, props) => [value, props.payload.tipo]}
                    labelFormatter={() => ''}
                  />
                  <Bar dataKey="qtd" name="Quantidade" fill="#2ecc71">
                    {Object.entries(dadosCarteiraFiltrados.tipos_bi).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CORES_GRAFICOS[index % CORES_GRAFICOS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center text-muted py-5">
                <i className="fas fa-inbox fa-3x mb-3"></i>
                <p>Nenhuma ferramenta BI registrada</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* NOVO: Motivos SEM ERP */}
<div className="col-lg-6">
  <div className="card shadow-sm h-100">
    <div className="card-header bg-white">
      <h5 className="card-title mb-0">
        <i className="fas fa-chart-bar me-2 text-warning"></i>
        Clientes SEM ERP - Motivos
      </h5>
    </div>
    <div className="card-body">
      {dadosCarteiraFiltrados?.motivos_sem_erp && Object.keys(dadosCarteiraFiltrados.motivos_sem_erp).length > 0 ? (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart 
            data={Object.entries(dadosCarteiraFiltrados.motivos_sem_erp)
              .map(([motivo, qtd]) => ({ motivo, qtd }))
              .sort((a, b) => b.qtd - a.qtd)}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis hide />
            <YAxis />
            <Tooltip 
              formatter={(value, name, props) => [value, props.payload.motivo]}
              labelFormatter={() => ''}
            />
            <Bar dataKey="qtd" name="Quantidade" fill="#f39c12">
              {Object.entries(dadosCarteiraFiltrados.motivos_sem_erp).map((entry, index) => (
                <Cell key={`cell-${index}`} fill={CORES_GRAFICOS[index % CORES_GRAFICOS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="text-center text-muted py-5">
          <i className="fas fa-inbox fa-3x mb-3"></i>
          <p>Todos os clientes possuem ERP</p>
        </div>
      )}
    </div>
  </div>
</div>

    {/* Potencial de BI */}
    <div className="row g-4">
      <div className="col-12">
        <div className="card shadow-sm border-warning">
          <div className="card-header bg-warning text-dark">
            <h5 className="card-title mb-0">
              <i className="fas fa-lightbulb me-2"></i>
              Potencial de Crescimento - BI
            </h5>
          </div>
          <div className="card-body">
            <div className="alert alert-warning mb-3">
              <div className="d-flex align-items-center">
                <i className="fas fa-chart-line fa-3x me-3 text-warning"></i>
                <div>
                  <h5 className="mb-1">
                    <strong>{dadosCarteiraFiltrados?.bi?.sem_bi || 0}</strong> clientes sem BI implementado
                  </h5>
                  <p className="mb-0">
                    Potencial mínimo de faturamento adicional: 
                    <strong className="text-success ms-2">
                      R$ {(dadosCarteiraFiltrados?.bi?.potencial_minimo || 0).toLocaleString('pt-BR')}
                    </strong>
                  </p>
                  <small className="text-muted">
                    * Considerando valor base de R$ 900,00 por cliente
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* TABELA DETALHADA: Clientes COM BI */}
    {(() => {
      const listaBI = dadosCarteiraFiltrados?.bi?.empresas_com_bi || [];
      const listaFiltrada = listaBI.filter(emp =>
        (!filtrosBi.empresa       || (emp.empresa       || '').toLowerCase().includes(filtrosBi.empresa.toLowerCase())) &&
        (!filtrosBi.numero_dominio|| (emp.numero_dominio|| '').toLowerCase().includes(filtrosBi.numero_dominio.toLowerCase())) &&
        (!filtrosBi.regime        || (emp.regime        || '').toLowerCase().includes(filtrosBi.regime.toLowerCase())) &&
        (!filtrosBi.operador      || (emp.operador      || '').toLowerCase().includes(filtrosBi.operador.toLowerCase())) &&
        (!filtrosBi.tipo_bi       || (emp.tipo_bi       || '').toLowerCase().includes(filtrosBi.tipo_bi.toLowerCase())) &&
        (!filtrosBi.bi_pago       || (emp.bi_pago       || '').toLowerCase().includes(filtrosBi.bi_pago.toLowerCase())) &&
        (!filtrosBi.link_bi       || (emp.link_bi       || '').toLowerCase().includes(filtrosBi.link_bi.toLowerCase())) &&
        (!filtrosBi.senha_bi      || (emp.senha_bi      || '').toLowerCase().includes(filtrosBi.senha_bi.toLowerCase())) &&
        (!filtrosBi.drive_cliente || (emp.drive_cliente || '').toLowerCase().includes(filtrosBi.drive_cliente.toLowerCase()))
      );
      const setFB = (campo, valor) => setFiltrosBi(prev => ({ ...prev, [campo]: valor }));
      const inputStyle = { fontSize: '0.75rem', padding: '2px 4px', minWidth: 0 };

      return (
        <div className="row g-4 mt-2">
          <div className="col-12">
            <div className="card shadow-sm border-success">
              <div className="card-header bg-success text-white d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">
                  <i className="fas fa-table me-2"></i>
                  Clientes com BI — Dados Completos
                  <span className="badge bg-white text-success ms-2">{listaFiltrada.length}</span>
                </h5>
                {Object.values(filtrosBi).some(v => v) && (
                  <button className="btn btn-sm btn-light" onClick={() => setFiltrosBi({ empresa: '', numero_dominio: '', regime: '', operador: '', tipo_bi: '', bi_pago: '', link_bi: '', senha_bi: '', drive_cliente: '' })}>
                    <i className="fas fa-eraser me-1"></i>Limpar filtros
                  </button>
                )}
              </div>
              <div className="card-body p-0">
                {listaBI.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover table-sm align-middle mb-0">
                      <thead className="table-success sticky-top">
                        <tr>
                          <th>Empresa</th>
                          <th>Nº Domínio</th>
                          <th>Regime</th>
                          <th>Operador</th>
                          <th>Tipo BI</th>
                          <th>BI Pago</th>
                          <th>Link BI</th>
                          <th>Senha BI</th>
                          <th>Drive Cliente</th>
                        </tr>
                        <tr>
                          {['empresa','numero_dominio','regime','operador','tipo_bi','bi_pago','link_bi','senha_bi','drive_cliente'].map(campo => (
                            <th key={campo} style={{ fontWeight: 'normal', padding: '4px' }}>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                style={inputStyle}
                                placeholder="Filtrar..."
                                value={filtrosBi[campo]}
                                onChange={e => setFB(campo, e.target.value)}
                              />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {listaFiltrada.length > 0 ? listaFiltrada.map((emp, index) => (
                          <tr key={index}>
                            <td className="fw-semibold">{emp.empresa}</td>
                            <td><code>{emp.numero_dominio || '-'}</code></td>
                            <td>
                              <span className="badge" style={{
                                backgroundColor:
                                  emp.regime?.toLowerCase().includes('presumido') ? '#3498db' :
                                  emp.regime?.toLowerCase().includes('simples') ? '#2ecc71' :
                                  emp.regime?.toLowerCase().includes('real') ? '#e74c3c' : '#95a5a6'
                              }}>
                                {emp.regime || '-'}
                              </span>
                            </td>
                            <td>{emp.operador || '-'}</td>
                            <td>{emp.tipo_bi || '-'}</td>
                            <td>
                              {emp.bi_pago ? (
                                <span className={`badge ${emp.bi_pago.toUpperCase() === 'SIM' ? 'bg-success' : 'bg-secondary'}`}>
                                  {emp.bi_pago}
                                </span>
                              ) : '-'}
                            </td>
                            <td>
                              {emp.link_bi ? (
                                <a href={emp.link_bi} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary py-0">
                                  <i className="fas fa-external-link-alt me-1"></i>Abrir
                                </a>
                              ) : '-'}
                            </td>
                            <td>
                              {emp.senha_bi ? (
                                <code className="text-secondary">{emp.senha_bi}</code>
                              ) : '-'}
                            </td>
                            <td>
                              {emp.drive_cliente ? (
                                <a href={emp.drive_cliente} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-secondary py-0">
                                  <i className="fas fa-folder-open me-1"></i>Drive
                                </a>
                              ) : '-'}
                            </td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan={9} className="text-center text-muted py-3">
                              <i className="fas fa-search me-2"></i>Nenhum resultado para os filtros aplicados
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="fas fa-inbox fa-3x mb-3"></i>
                    <p>Nenhum cliente com BI encontrado</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    })()}
  </div>
)}

          {/* ABA OPERADORES */}
          {abaAtiva === 'operadores' && (
            <div className="tab-pane-custom">
              {/* Atendimento por Operador */}
              <div className="row g-4 mb-4">
                <div className="col-12">
                  <div className="card shadow-sm">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-chart-bar me-2 text-primary"></i>
                        Atendimento por Colaborador (Top 10)
                      </h5>
                    </div>
                    <div className="card-body">
                      {dadosAtendimentoOperador.length > 0 ? (
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart 
                            data={dadosAtendimentoOperador}
                            margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="operador" 
                              angle={-45} 
                              textAnchor="end" 
                              height={100}
                              interval={0}
                            />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="total" name="Total de Clientes" fill="#3498db" />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center text-muted py-5">
                          <i className="fas fa-inbox fa-3x mb-3"></i>
                          <p>Nenhum dado disponível</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Fechamentos por Data (DT) */}
              <div className="row g-4 mb-4">
                <div className="col-12">
                  <div className="card shadow-sm">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-calendar-alt me-2 text-primary"></i>
                        Fechamentos por Data e Colaborador (DT)
                      </h5>
                    </div>
                    <div className="card-body">
                      {dadosCarteiraFiltrados?.fechamentos_por_dt && dadosCarteiraFiltrados.fechamentos_por_dt.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-hover align-middle">
                            <thead className="table-light">
                              <tr>
                                <th>Operador</th>
                                <th className="text-center">Data Mais Comum (DT)</th>
                                <th className="text-center">Qtd nessa Data</th>
                                <th>Distribuição de Datas</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dadosCarteiraFiltrados.fechamentos_por_dt.map((item, index) => (
                                <tr key={index}>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <div 
                                        className="operador-avatar me-2"
                                        style={{ backgroundColor: CORES_GRAFICOS[index % CORES_GRAFICOS.length] }}
                                      >
                                        {item.operador.charAt(0).toUpperCase()}
                                      </div>
                                      <span className="fw-semibold">{item.operador}</span>
                                    </div>
                                  </td>
                                  <td className="text-center">
                                    <span className="badge bg-primary fs-6">Dia {item.dt_mais_comum}</span>
                                  </td>
                                  <td className="text-center">
                                    <span className="badge bg-success fs-6">{item.total_nessa_data} clientes</span>
                                  </td>
                                  <td>
                                    <div className="d-flex flex-wrap gap-1">
                                      {Object.entries(item.distribuicao_datas)
                                        .sort((a, b) => b[1] - a[1])
                                        .slice(0, 5)
                                        .map(([dt, qtd], idx) => (
                                          <span key={idx} className="badge bg-secondary">
                                            Dia {dt}: {qtd}
                                          </span>
                                        ))}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center text-muted py-5">
                          <i className="fas fa-inbox fa-3x mb-3"></i>
                          <p>Nenhum dado disponível</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Detalhamento por Operador */}
              <div className="row g-4">
                <div className="col-12">
                  <div className="card shadow-sm">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-list me-2 text-primary"></i>
                        Detalhamento por Operador
                      </h5>
                    </div>
                    <div className="card-body">
                      {dadosCarteiraFiltrados?.atendimento_por_operador && Object.keys(dadosCarteiraFiltrados.atendimento_por_operador).length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-hover align-middle">
                            <thead className="table-dark">
                              <tr>
                                <th>Operador</th>
                                <th className="text-center">Total</th>
                                <th className="text-center">LP</th>
                                <th className="text-center">SN</th>
                                <th className="text-center">LR</th>
                                <th className="text-center">LRT</th>
                                {Object.values(dadosCarteiraFiltrados.atendimento_por_operador).some(op => op.regimes.NAO_INFORMADO > 0) && (
                                  <th className="text-center">N/I</th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(dadosCarteiraFiltrados.atendimento_por_operador)
                                .sort((a, b) => b[1].total_clientes - a[1].total_clientes)
                                .map(([operador, dados], index) => (
                                  <tr key={operador}>
                                    <td>
                                      <div className="d-flex align-items-center">
                                        <div 
                                          className="operador-avatar me-2"
                                          style={{ backgroundColor: CORES_GRAFICOS[index % CORES_GRAFICOS.length] }}
                                        >
                                          {operador.charAt(0).toUpperCase()}
                                        </div>
                                        <span className="fw-semibold">{operador}</span>
                                      </div>
                                    </td>
                                    <td className="text-center">
                                      <span className="badge bg-secondary fs-6">{dados.total_clientes}</span>
                                    </td>
                                    <td className="text-center">
                                      <span className="badge" style={{ backgroundColor: CORES_REGIMES.LP }}>
                                        {dados.regimes.LP}
                                      </span>
                                    </td>
                                    <td className="text-center">
                                      <span className="badge" style={{ backgroundColor: CORES_REGIMES.SN }}>
                                        {dados.regimes.SN}
                                      </span>
                                    </td>
                                    <td className="text-center">
                                      <span className="badge" style={{ backgroundColor: CORES_REGIMES.LR }}>
                                        {dados.regimes.LR}
                                      </span>
                                    </td>
                                    <td className="text-center">
                                      <span className="badge" style={{ backgroundColor: CORES_REGIMES.LRT }}>
                                        {dados.regimes.LRT}
                                      </span>
                                    </td>
                                    {Object.values(dadosCarteiraFiltrados.atendimento_por_operador).some(op => op.regimes.NAO_INFORMADO > 0) && (
                                      <td className="text-center">
                                        <span className="badge bg-secondary">
                                          {dados.regimes.NAO_INFORMADO || 0}
                                        </span>
                                      </td>
                                    )}
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center text-muted py-5">
                          <i className="fas fa-inbox fa-3x mb-3"></i>
                          <p>Nenhum dado disponível</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ABA AUDITORIA */}
          {abaAtiva === 'auditoria' && (
            <div className="tab-pane-custom">
              {/* KPIs Auditoria */}
              <div className="row g-4 mb-4">
                <div className="col-lg-3 col-md-6">
                  <div className="kpi-card kpi-primary">
                    <div className="kpi-icon">
                      <i className="fas fa-clipboard-check"></i>
                    </div>
                    <div className="kpi-content">
                      <h3 className="kpi-value">{dadosCarteiraFiltrados?.auditoria?.externa || 0}</h3>
                      <p className="kpi-label">Auditoria Externa</p>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6">
                  <div className="kpi-card kpi-info">
                    <div className="kpi-icon">
                      <i className="fas fa-clipboard-check"></i>
                    </div>
                    <div className="kpi-content">
                      <h3 className="kpi-value">{dadosCarteiraFiltrados?.auditoria?.interna || 0}</h3>
                      <p className="kpi-label">Auditoria Interna</p>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6">
                  <div className="kpi-card kpi-success">
                    <div className="kpi-icon">
                      <i className="fas fa-check-double"></i>
                    </div>
                    <div className="kpi-content">
                      <h3 className="kpi-value">{dadosCarteiraFiltrados?.auditoria?.ambas || 0}</h3>
                      <p className="kpi-label">Ambas</p>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6">
                  <div className="kpi-card kpi-secondary">
                    <div className="kpi-icon">
                      <i className="fas fa-times"></i>
                    </div>
                    <div className="kpi-content">
                      <h3 className="kpi-value">{dadosCarteiraFiltrados?.auditoria?.nenhuma || 0}</h3>
                      <p className="kpi-label">Sem Auditoria</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Gráfico de Auditoria */}
              <div className="row g-4 mb-4">
                <div className="col-lg-6">
                  <div className="card shadow-sm h-100">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-chart-pie me-2 text-primary"></i>
                        Distribuição de Auditoria
                      </h5>
                    </div>
                    <div className="card-body">
                      {dadosCarteiraFiltrados?.auditoria ? (
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Auditoria Externa', value: dadosCarteiraFiltrados.auditoria.externa, color: '#3498db' },
                                { name: 'Auditoria Interna', value: dadosCarteiraFiltrados.auditoria.interna, color: '#17a2b8' },
                                { name: 'Ambas', value: dadosCarteiraFiltrados.auditoria.ambas, color: '#28a745' },
                                { name: 'Sem Auditoria', value: dadosCarteiraFiltrados.auditoria.nenhuma, color: '#95a5a6' }
                              ].filter(item => item.value > 0)}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={100}
                              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            >
                              {[
                                { color: '#3498db' },
                                { color: '#17a2b8' },
                                { color: '#28a745' },
                                { color: '#95a5a6' }
                              ].map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center text-muted py-5">
                          <p>Sem dados</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="card shadow-sm h-100 bg-light">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-info-circle me-2 text-info"></i>
                        Informações sobre Auditoria
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="alert alert-info">
                        <h6 className="alert-heading">
                          <i className="fas fa-lightbulb me-2"></i>
                          Sobre Auditoria
                        </h6>
                        <ul className="mb-0">
                          <li>
                            <strong>Auditoria Externa:</strong> Clientes que possuem auditoria realizada por empresas externas
                          </li>
                          <li>
                            <strong>Auditoria Interna:</strong> Clientes com auditoria realizada internamente pela empresa
                          </li>
                          <li>
                            <strong>Ambas:</strong> Clientes que possuem tanto auditoria externa quanto interna
                          </li>
                          <li>
                            <strong>Sem Auditoria:</strong> Clientes que não possuem nenhum tipo de auditoria implementada
                          </li>
                        </ul>
                      </div>

                      <div className="mt-3">
                        <h6 className="fw-bold mb-2">
                          <i className="fas fa-chart-bar me-2 text-success"></i>
                          Percentual com Auditoria
                        </h6>
                        {dadosCarteiraFiltrados?.auditoria && dadosCarteiraFiltrados.total_geral > 0 && (
                          <div className="progress" style={{ height: '30px' }}>
                            <div 
                              className="progress-bar bg-success" 
                              style={{ 
                                width: `${((dadosCarteiraFiltrados.auditoria.externa + dadosCarteiraFiltrados.auditoria.interna + dadosCarteiraFiltrados.auditoria.ambas) / dadosCarteiraFiltrados.total_geral * 100).toFixed(1)}%` 
                              }}
                            >
                              {((dadosCarteiraFiltrados.auditoria.externa + dadosCarteiraFiltrados.auditoria.interna + dadosCarteiraFiltrados.auditoria.ambas) / dadosCarteiraFiltrados.total_geral * 100).toFixed(1)}%
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detalhes de Empresas com Auditoria */}
              {dadosCarteiraFiltrados?.auditoria?.detalhes && dadosCarteiraFiltrados.auditoria.detalhes.length > 0 && (
                <div className="row g-4">
                  <div className="col-12">
                    <div className="card shadow-sm">
                      <div className="card-header bg-white">
                        <h5 className="card-title mb-0">
                          <i className="fas fa-list me-2 text-primary"></i>
                          Empresas com Auditoria
                        </h5>
                      </div>
                      <div className="card-body">
                        <div className="table-responsive">
                          <table className="table table-hover align-middle">
                            <thead className="table-light">
                              <tr>
                                <th>Empresa</th>
                                <th className="text-center">Auditoria Externa</th>
                                <th className="text-center">Auditoria Interna</th>
                                <th>Operador</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dadosCarteiraFiltrados.auditoria.detalhes.map((item, index) => (
                                <tr key={index}>
                                  <td className="fw-semibold">{item.empresa}</td>
                                  <td className="text-center">
                                    {item.auditoria_externa === 'SIM' ? (
                                      <span className="badge bg-success">
                                        <i className="fas fa-check me-1"></i>
                                        Sim
                                      </span>
                                    ) : (
                                      <span className="badge bg-secondary">
                                        <i className="fas fa-times me-1"></i>
                                        Não
                                      </span>
                                    )}
                                  </td>
                                  <td className="text-center">
                                    {item.auditoria_interna === 'SIM' ? (
                                      <span className="badge bg-info">
                                        <i className="fas fa-check me-1"></i>
                                        Sim
                                      </span>
                                    ) : (
                                      <span className="badge bg-secondary">
                                        <i className="fas fa-times me-1"></i>
                                        Não
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    <span className="badge bg-primary">{item.operador}</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CarteiraContabil;